import { ConsolidationPackingSortDimension } from "@redotech/crdb-enums/fulfillment/consolidation-packing-sort-dimension";
import * as z from "zod";

export const PackingSortDimension = ConsolidationPackingSortDimension;

export type PackingSortDimension = ConsolidationPackingSortDimension;

export const PackingSortDimensionSchema = z.enum(PackingSortDimension);

export const PackingSortDimensionsSchema = z
  .array(PackingSortDimensionSchema)
  .min(1)
  .max(3)
  .refine((dimensions) => new Set(dimensions).size === dimensions.length, {
    message: "Packing sort dimensions must be unique",
  });

export const PackingPlanSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("unconfigured") }),
  z.object({ type: z.literal("open") }),
  z.object({
    dimensions: PackingSortDimensionsSchema,
    type: z.literal("grouped"),
  }),
]);

export type PackingPlan = z.infer<typeof PackingPlanSchema>;

export const PackingGroupPartSchema = z.object({
  displayValue: z.string().nullable().optional(),
  dimension: PackingSortDimensionSchema,
  value: z.string().nullable(),
});

export type PackingGroupPart = z.infer<typeof PackingGroupPartSchema>;

export const PackingGroupPartsSchema = z.array(PackingGroupPartSchema).min(1);

export interface PackingItemFacts {
  grade: string | null;
  productId: string | null;
  productLabel: string | null;
}

export interface PackingGroup {
  key: string;
  label: string;
  parts: PackingGroupPart[];
}

export function classifyPackingItem(
  dimensions: readonly PackingSortDimension[],
  facts: PackingItemFacts,
): PackingGroup {
  const parsedDimensions = PackingSortDimensionsSchema.parse(dimensions);
  const parts = parsedDimensions.map((dimension) =>
    partForDimension(dimension, facts),
  );
  return {
    key: createPackingGroupKey(parts),
    label: formatPackingGroupLabel(parts),
    parts,
  };
}

export function createPackingGroupKey(
  parts: readonly PackingGroupPart[],
): string {
  const parsedParts = PackingGroupPartsSchema.parse(parts);
  return JSON.stringify(
    parsedParts.map(({ dimension, value }) => [dimension, value]),
  );
}

export function formatPackingGroupLabel(
  parts: readonly PackingGroupPart[],
): string {
  return PackingGroupPartsSchema.parse(parts)
    .map(({ dimension, displayValue, value }) =>
      value === null
        ? `No ${dimensionLabel(dimension).toLowerCase()}`
        : formatPackingGroupPart(dimension, displayValue ?? value),
    )
    .join(" · ");
}

function formatPackingGroupPart(
  dimension: PackingSortDimension,
  value: string,
): string {
  return dimension === PackingSortDimension.Grade ? `Grade ${value}` : value;
}

function partForDimension(
  dimension: PackingSortDimension,
  facts: PackingItemFacts,
): PackingGroupPart {
  switch (dimension) {
    case PackingSortDimension.Grade:
      return { dimension, value: facts.grade };
    case PackingSortDimension.Product:
      return {
        dimension,
        displayValue: facts.productLabel,
        value: facts.productId,
      };
  }
}

function dimensionLabel(dimension: PackingSortDimension): string {
  switch (dimension) {
    case PackingSortDimension.Grade:
      return "Grade";
    case PackingSortDimension.Product:
      return "Product";
  }
}
